import { ref } from 'vue';

// Ellie Desk: shared state for the copilot panel's suggestion context card
// (core-touch, see custom/README.md). The inline reply-suggestion flow stashes
// the grounding context returned by ellie-desk-ai here; the copilot panel
// reads it for the current conversation and falls back to the conversation's
// latest auto-suggest private note (content_attributes.ellie_suggestion)
// when unset.

// Module-level ref so every consumer shares one instance.
const suggestionContext = ref(null); // { conversationId, context } | null

/**
 * Normalizes the two grounding-context shapes into one card-ready object:
 * - reply_suggestion task response: { citations, agent_notes, confidence }
 * - ellie_suggestion note attrs:    { citations, agentNotes, confidence, … }
 * @param {Object|null} raw
 * @returns {{citations: Array, agentNotes: string, confidence: number|null}|null}
 */
export function normalizeSuggestionContext(raw) {
  if (!raw) return null;
  const citations = Array.isArray(raw.citations) ? raw.citations : [];
  const agentNotes = raw.agentNotes ?? raw.agent_notes ?? '';
  const confidence = typeof raw.confidence === 'number' ? raw.confidence : null;
  if (!citations.length && !agentNotes && confidence === null) return null;
  return { citations, agentNotes, confidence };
}

export function useCopilotSuggestionContext() {
  /**
   * Stashes the grounding context of a fresh inline reply suggestion.
   * @param {number} conversationId
   * @param {Object|null} context - raw context from the task response
   */
  const setSuggestionContext = (conversationId, context) => {
    const normalized = normalizeSuggestionContext(context);
    suggestionContext.value = normalized
      ? { conversationId, context: normalized }
      : null;
  };

  return { suggestionContext, setSuggestionContext };
}
